import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import CalendarMonthIcon from "@material-ui/icons/CalendarTodayTwoTone";
import CloseIcon from "@material-ui/icons/Close";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import * as locales from "react-date-range/dist/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { toast } from "react-toastify";
import Tooltip from "../../../common/Tooltip";

try {
  const heatmapModule = require("highcharts/modules/heatmap");
  if (typeof heatmapModule === "function") {
    heatmapModule(Highcharts);
  } else if (heatmapModule && typeof heatmapModule.default === "function") {
    heatmapModule.default(Highcharts);
  }
} catch (e) {
  console.error("Highcharts heatmap module error:", e);
}

const MovingAverageChart = ({
  data,
  handleSubmit = () => { },
  selectedOptions,
  onDateRangeChange = () => { },
  date,
  setDate = () => { },
  onViewRoute = () => { },
  onInsertIndexData,
}) => {
  const [period, setPeriod] = useState(1);
  const [periods, setPeriods] = useState([1]);
  const [smaOptions, setSmaOptions] = useState({});
  const [normalOptions, setNormalOptions] = useState({});
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  const [dailyChangeOptions, setDailyChangeOptions] = useState({});
  const [volatilityOptions, setVolatilityOptions] = useState({});
  const locale = "enUS";
  const [heatmapOptions, setHeatmapOptions] = useState({});
  const [seasonalHeatmapOptions, setSeasonalHeatmapOptions] = useState({});
  const [seasonalOptions, setSeasonalOptions] = useState({});

  useEffect(() => {
    if (data.length === 0 || selectedOptions.length === 0) return;
    const heatmapData = computeCorrelationMatrix(data, selectedOptions);
    const values = heatmapData.flat();
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    setHeatmapOptions({
      chart: {
        type: "heatmap",
        height: 350,
        plotBorderWidth: 0,
        backgroundColor: "white",
      },
      title: {
        text: "Correlation Heatmap",
        style: {
          fontSize: "12px",
          fontWeight: "600",
          color: "#1e293b",
        },
      },
      xAxis: {
        categories: selectedOptions,
        visible: false,
      },
      yAxis: {
        categories: selectedOptions,
        title: null,
        labels: {
          style: {
            fontSize: "10px",
            color: "#555",
          },
        },
        lineWidth: 0,
        tickWidth: 0,
        gridLineWidth: 0,
        reversed: true,
      },
      colorAxis: {
        min: minVal,
        max: maxVal,
        stops: [
          [0, "#40000a"],
          [0.1, "#60000d"],
          [0.2, "#800010"],
          [0.3, "#a00014"],
          [0.4, "#c00018"],
          [0.5, "#e0001c"],
          [0.6, "#ff1a2b"],
          [0.7, "#ff4d4d"],
          [0.8, "#ff8080"],
          [1, "#ffc0c0"],
        ],
        labels: {
          style: { fontSize: "10px", color: "#444" },
          format: "{value:.2f}",
        },
        title: {
          text: "Correlation (ρ)",
          style: { fontSize: "11px", color: "#333", fontWeight: "600" },
          margin: 10,
        },
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        borderRadius: 0,
        padding: 8,
        style: {
          color: "#fff",
          fontSize: "11px",
        },
        formatter: function () {
          return `
            <div>
              <b>${selectedOptions[this.point.x]}</b> vs <b>${selectedOptions[this.point.y]
            }</b><br/>
              Correlation: <b>${this.point.value.toFixed(3)}</b>
            </div>
          `;
        },
      },
      series: [
        {
          borderWidth: 0.1,
          borderColor: "rgba(255,150,150,0.5)",
          data: heatmapData,
          dataLabels: {
            format: "{value:.2f}",
            color: "#fff",
            stroke: "#fff",
            strokeWidth: 0,
            enabled: true,
          },
        },
      ],
    });
  }, [data, selectedOptions]);

  useEffect(() => {
    if (selectedOptions.length === 0) return;

    try {
      const { normalSeries, maSeries } = generateDualSeries(data);

      const maChartOptions = {
        chart: { type: "line", height: 260, zooming: { type: "xy" } },
        title: { text: "Moving Averages Chart", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
        subtitle: { text: `Multiple Period Moving Averages`, style: { fontSize: "10px", color: "#64748b" } },
        xAxis: { type: "datetime", labels: { format: "{value:%Y-%m-%d}", style: { fontSize: "10px" } } },
        yAxis: { title: { text: "Value", style: { fontSize: "10px" } }, labels: { style: { fontSize: "10px" } } },
        series: maSeries,
        plotOptions: { series: { turboThreshold: 0, connectNulls: true } },
        legend: { enabled: true, itemStyle: { fontSize: "10px" } },
        tooltip: {
          style: { fontSize: "11px" },
          formatter: function () {
            const date = new Date(this.x);
            const day = Highcharts.dateFormat("%A", date);
            const month = Highcharts.dateFormat("%b", date);
            const dayOfMonth = Highcharts.dateFormat("%d", date);
            const year = Highcharts.dateFormat("%Y", date);
            const time = Highcharts.dateFormat("%I:%M %p", date);
            return `
              <p style="fontSize:10px">${year} ${month} ${dayOfMonth}, ${day}, ${time}</p><br/>
              <span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${this.y}</b>
            `;
          },
        },
      };

      const normalChartOptions = {
        chart: { type: "line", height: 260, zooming: { type: "xy" } },
        title: { text: "Raw Data Chart", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
        subtitle: { text: "Original Data Values", style: { fontSize: "10px", color: "#64748b" } },
        xAxis: { type: "datetime", labels: { format: "{value:%Y-%m-%d}", style: { fontSize: "10px" } } },
        yAxis: { title: { text: "Value", style: { fontSize: "10px" } }, labels: { style: { fontSize: "10px" } } },
        series: normalSeries,
        plotOptions: { series: { turboThreshold: 0, connectNulls: true } },
        legend: { enabled: true, itemStyle: { fontSize: "10px" } },
        tooltip: {
          style: { fontSize: "11px" },
          formatter: function () {
            const date = new Date(this.x);
            const day = Highcharts.dateFormat("%A", date);
            const month = Highcharts.dateFormat("%b", date);
            const dayOfMonth = Highcharts.dateFormat("%d", date);
            const year = Highcharts.dateFormat("%Y", date);
            const time = Highcharts.dateFormat("%I:%M %p", date);
            return `
              <p style="fontSize:10px">${year} ${month} ${dayOfMonth}, ${day}, ${time}</p><br/>
              <span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${this.y}</b>
            `;
          },
        },
      };

      setSmaOptions(maChartOptions);
      setNormalOptions(normalChartOptions);
    } catch (error) {
      console.error("Error generating charts:", error);
    }
  }, [data, periods]);

  const computeMonthlyAverage = (data, keys) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthlyData = keys.map((key) => {
      const monthSums = Array(12).fill(0);
      const monthCounts = Array(12).fill(0);

      data.forEach((item) => {
        const date = new Date(item.Date);
        const month = date.getMonth();
        if (key in item && typeof item[key] === "number" && !isNaN(item[key])) {
          monthSums[month] += item[key];
          monthCounts[month] += 1;
        }
      });

      const averages = monthSums.map((sum, idx) =>
        monthCounts[idx] > 0 ? sum / monthCounts[idx] : null
      );

      return {
        name: key,
        data: averages,
      };
    });

    return { series: monthlyData, categories: monthNames };
  };

  useEffect(() => {
    if (!data || data.length === 0 || selectedOptions.length === 0) return;

    const { series, categories } = computeMonthlyAverage(data, selectedOptions);

    setSeasonalOptions({
      chart: { type: "line", height: 320 },
      title: { text: "Average Seasonal Pattern", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
      xAxis: { categories, labels: { style: { fontSize: "10px" } } },
      yAxis: { title: { text: "Value", style: { fontSize: "10px" } }, labels: { style: { fontSize: "10px" } } },
      series,
      tooltip: {
        style: { fontSize: "11px" },
        formatter: function () {
          return `<b>${this.series.name}</b>: <b>${this.y?.toFixed(2)}</b>`;
        },
      },
      plotOptions: { series: { connectNulls: true } },
      legend: { enabled: true, itemStyle: { fontSize: "10px" } },
    });
  }, [data, selectedOptions]);

  const computeYearlySeasonality = (data, keys) => {
    const yearSet = new Set();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    data.forEach((d) => {
      const year = new Date(d.Date).getFullYear();
      yearSet.add(year);
    });

    const years = Array.from(yearSet).sort((a, b) => a - b);

    const seriesData = [];

    keys.forEach((key, keyIndex) => {
      years.forEach((year, yIndex) => {
        monthNames.forEach((month, mIndex) => {
          const monthNum = mIndex;
          const valueObj = data.find((d) => {
            const dDate = new Date(d.Date);
            return (
              dDate.getFullYear() === year &&
              dDate.getMonth() === monthNum &&
              d[key] != null
            );
          });

          const value = valueObj ? valueObj[key] : null;

          seriesData.push([mIndex, yIndex, value]);
        });
      });
    });

    return { seriesData, years, months: monthNames };
  };

  useEffect(() => {
    if (!data || data.length === 0 || selectedOptions.length === 0) return;

    const { seriesData, years, months } = computeYearlySeasonality(
      data,
      selectedOptions
    );

    const values = seriesData.map((d) => d[2]).filter((v) => v != null);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    setSeasonalHeatmapOptions({
      chart: { type: "heatmap", height: 350 },
      title: { text: "Yearly Seasonality Heatmap", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
      xAxis: { categories: months, labels: { style: { fontSize: "10px" } } },
      yAxis: { categories: years, reversed: true, labels: { style: { fontSize: "10px" } } },
      colorAxis: {
        min: minVal,
        max: maxVal,
        stops: [
          [0, "#40000a"],
          [0.1, "#60000d"],
          [0.2, "#800010"],
          [0.3, "#a00014"],
          [0.4, "#c00018"],
          [0.5, "#e0001c"],
          [0.6, "#ff1a2b"],
          [0.7, "#ff4d4d"],
          [0.8, "#ff8080"],
          [1, "#ffc0c0"],
        ],
        labels: { format: "{value:.2f}", style: { fontSize: "10px" } },
      },
      tooltip: {
        style: { fontSize: "11px" },
        formatter: function () {
          return `<b>${months[this.point.x]} ${years[this.point.y]
            }</b>: <b>${this.point.value?.toFixed(2)}</b>`;
        },
      },
      series: [
        {
          data: seriesData,
          borderWidth: 0.1,
          borderColor: "#fff",
          dataLabels: { enabled: false },
        },
      ],
    });
  }, [data, selectedOptions]);

  const calculateSMA = (data, period) => {
    const result = [];
    if (data.length < period) return result;
    for (let i = period - 1; i < data.length; i++) {
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += data[i - j][1];
      }
      result.push([data[i][0], sum / period]);
    }
    return result;
  };

  const generateDualSeries = (inputData) => {
    const seriesData = {};
    selectedOptions.forEach((key) => (seriesData[key] = []));

    inputData.forEach((item) => {
      const timestamp = new Date(item.Date).getTime();
      selectedOptions.forEach((key) => {
        if (key in item && typeof item[key] === "number" && !isNaN(item[key])) {
          seriesData[key].push([timestamp, item[key]]);
        }
      });
    });

    Object.keys(seriesData).forEach((k) => {
      seriesData[k].sort((a, b) => a[0] - b[0]);
    });

    const colors = [
      "#313C4C", // Primary Theme Blue
      "#f7a35c",
      "#90ed7d",
      "#434348",
      "#8085e9",
      "#f15c80",
    ];
    const normalSeries = [];
    const maSeries = [];

    Object.keys(seriesData).forEach((key, index) => {
      const colorIndex = index % colors.length;
      if (!seriesData[key].length) return;

      normalSeries.push({
        name: key,
        data: seriesData[key],
        color: colors[colorIndex],
        visible: true,
        turboThreshold: 0,
      });

      maSeries.push({
        name: key,
        data: seriesData[key],
        color: colors[colorIndex],
        visible: false,
        turboThreshold: 0,
      });

      periods.forEach((periodValue, idx) => {
        if (seriesData[key].length >= periodValue) {
          const smaData = calculateSMA(seriesData[key], periodValue);
          maSeries.push({
            name: `${key} (${periodValue} period SMA)`,
            data: smaData,
            color: colors[(index + idx + 1) % colors.length],
            dashStyle: "ShortDash",
            visible: true,
            lineWidth: 2,
            marker: { enabled: false },
          });
        }
      });
    });

    return { normalSeries, maSeries };
  };

  const handleDateRangeSelect = (ranges) => {
    const selectedRange = ranges.selection;
    setDateRange([selectedRange]);

    if (
      selectedRange.startDate &&
      selectedRange.endDate &&
      selectedRange.startDate.getTime() !== selectedRange.endDate.getTime()
    ) {
      setDate({
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      });
      onDateRangeChange({
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      });
      setOpenDatePicker(false);
    }
  };

  const handleApplyDateRange = () => {
    const selectedRange = dateRange[0];
    if (
      selectedRange.startDate &&
      selectedRange.endDate
    ) {
      setDate({
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      });
      onDateRangeChange({
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      });
    }
    setOpenDatePicker(false);
  };

  const generateDailyChangeSeries = (inputData, metric) => {
    const result = [];
    for (let i = 1; i < inputData.length; i++) {
      const prev = inputData[i - 1][metric];
      const curr = inputData[i][metric];
      if (prev && curr) {
        const change = ((curr - prev) / prev) * 100;
        result.push([new Date(inputData[i].Date).getTime(), change]);
      }
    }
    return result;
  };

  const computeCorrelationMatrix = (data, keys) => {
    const matrix = [];
    const getVals = (key) =>
      data
        .map((row) => row[key])
        .filter((val) => typeof val === "number" && !isNaN(val));

    const pearson = (arr1, arr2) => {
      const n = arr1.length;
      if (n !== arr2.length || n === 0) return 0;
      const mean1 = arr1.reduce((s, a) => s + a, 0) / n;
      const mean2 = arr2.reduce((s, a) => s + a, 0) / n;
      const numerator = arr1.reduce(
        (sum, a, i) => sum + (a - mean1) * (arr2[i] - mean2),
        0
      );
      const denominator = Math.sqrt(
        arr1.reduce((sum, a) => sum + Math.pow(a - mean1, 2), 0) *
        arr2.reduce((sum, a) => sum + Math.pow(a - mean2, 2), 0)
      );
      return denominator === 0 ? 0 : numerator / denominator;
    };

    keys.forEach((key1, i) => {
      keys.forEach((key2, j) => {
        const vals1 = getVals(key1);
        const vals2 = getVals(key2);
        matrix.push([i, j, pearson(vals1, vals2)]);
      });
    });

    return matrix;
  };

  const calculateRollingVolatility = (data, metric, windowSize = 20) => {
    const result = [];
    for (let i = windowSize - 1; i < data.length; i++) {
      const slice = data.slice(i - windowSize + 1, i + 1);
      const values = slice
        .map((d) => d[metric])
        .filter((v) => typeof v === "number" && !isNaN(v));
      if (values.length < windowSize) continue;

      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance =
        values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length;
      const volatility = Math.sqrt(variance);
      result.push([new Date(data[i].Date).getTime(), volatility]);
    }
    return result;
  };

  useEffect(() => {
    if (!data || data.length === 0 || selectedOptions.length === 0) return;

    const series = selectedOptions.map((key) => ({
      name: key,
      data: calculateRollingVolatility(data, key, 20),
      color: "#313C4C", // Updated to theme color
      marker: { enabled: false },
      lineWidth: 1.5,
    }));

    setVolatilityOptions({
      chart: { type: "line", height: 260, zooming: { type: "x" } },
      title: { text: "Rolling Volatility", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
      xAxis: { type: "datetime", labels: { style: { fontSize: "10px" } } },
      yAxis: { title: { text: "Volatility", style: { fontSize: "10px" } }, labels: { style: { fontSize: "10px" } } },
      series,
      plotOptions: { series: { turboThreshold: 0, connectNulls: true } },
      tooltip: {
        style: { fontSize: "11px" },
        formatter: function () {
          return `<b>${this.series.name}</b><br/>${Highcharts.dateFormat(
            "%b %d, %Y",
            this.x
          )}: ${this.y.toFixed(2)}`;
        },
      },
      legend: { enabled: true, itemStyle: { fontSize: "10px" } },
    });
  }, [data, selectedOptions]);

  useEffect(() => {
    if (!data || data.length === 0 || selectedOptions.length === 0) return;

    const series = selectedOptions.map((key) => ({
      name: key,
      data: generateDailyChangeSeries(data, key),
      color: "#313C4C", // Updated to theme color
      marker: { enabled: false },
      lineWidth: 1,
    }));

    setDailyChangeOptions({
      chart: { type: "line", height: 260, zooming: { type: "x" } },
      title: { text: "Daily % Change", style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
      xAxis: { type: "datetime", labels: { style: { fontSize: "10px" } } },
      yAxis: {
        title: { text: "Daily % Change", style: { fontSize: "10px" } },
        labels: {
          style: { fontSize: "10px" },
          formatter: function () {
            return this.value.toFixed(1) + "%";
          },
        },
      },
      tooltip: {
        valueSuffix: "%",
        style: { fontSize: "11px" },
        formatter: function () {
          return `<b>${this.series.name}</b><br/>${Highcharts.dateFormat(
            "%b %d, %Y",
            this.x
          )}: ${this.y.toFixed(2)}%`;
        },
      },
      series,
      plotOptions: { series: { turboThreshold: 0, connectNulls: true } },
      legend: { enabled: false },
    });
  }, [data, selectedOptions]);

  useEffect(() => {
    if (openDatePicker && date?.startDate && date?.endDate) {
      setDateRange([
        {
          startDate: new Date(date.startDate),
          endDate: new Date(date.endDate),
          key: "selection",
        },
      ]);
    }
  }, [openDatePicker, date]);

  const handleReset = () => {
    const newDateRange = [
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ];
    setDateRange(newDateRange);
    const defaultStart = moment().subtract(10, "years").toISOString();
    const defaultEnd = moment().toISOString();
    onDateRangeChange({
      startDate: defaultStart,
      endDate: defaultEnd,
    });
  };

  return (
    <div className="min-w-0">
      <div className="p-2 bg-white rounded-none border border-slate-300 mb-2">
        <div className="flex flex-wrap items-end justify-between w-full gap-2">
          {/* Left Controls: Moving Average, Date Range, Submit */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-fit">
              <Tooltip text="Select the number of days to calculate the Simple Moving Average (SMA).">
                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">
                  Moving average
                </label>
              </Tooltip>

              <div className="flex flex-wrap gap-1">
                {["1", "5", "10", "20", "30"].map((val) => (
                  <button
                    key={val}
                    type="button"
                    aria-pressed={periods.includes(parseInt(val))}
                    onClick={() => {
                      if (periods.includes(parseInt(val))) {
                        setPeriods(periods.filter((p) => p !== parseInt(val)));
                      } else {
                        setPeriods([...periods, parseInt(val)]);
                      }
                    }}
                    className={`h-7 px-2.5 rounded-none text-[11px] font-medium border transition ${periods.includes(parseInt(val))
                      ? "bg-[#313C4C] text-white border-[#0a2a47]"
                      : "bg-white text-slate-700 border-slate-300 hover:border-[#313C4C] hover:bg-sky-50"
                      }`}
                  >
                    {val}d
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Picker */}
            <div className="w-full sm:w-72 min-w-0">
              <label className="block text-[11px] font-medium text-slate-700 mb-0.5">
                Date Range
              </label>
              <div
                className="w-full h-7 px-2 border border-slate-300 rounded-none flex justify-between items-center cursor-pointer bg-white hover:border-slate-400 focus:border-[#313C4C]"
                onClick={() => setOpenDatePicker(true)}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    setOpenDatePicker(true);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Select date range"
                aria-expanded={openDatePicker}
                aria-haspopup="dialog"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <CalendarMonthIcon style={{ fontSize: 15, color: "#313C4C" }} />

                  {date?.startDate && date?.endDate ? (
                    <span className="text-[11px] font-medium text-slate-700 truncate">
                      {moment(date.startDate).format("MMM D, YYYY")} –{" "}
                      {moment(date.endDate).format("MMM D, YYYY")}
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-slate-500">
                      Select a date range
                    </span>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    handleReset();
                    e.stopPropagation();
                  }}
                  className="shrink-0 ml-1 text-slate-500 hover:text-rose-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#313C4C]"
                  aria-label="Reset date range"
                >
                  <CloseIcon style={{ fontSize: 15 }} />
                </button>
              </div>
            </div>

            {/* Submit & Insert Index Data Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                className={`h-7 px-3 bg-[#313C4C] text-white text-[11px] font-medium rounded-none whitespace-nowrap transition-colors ${selectedOptions?.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : "opacity-100 hover:bg-[#1e2632] cursor-pointer shadow-sm"
                  }`}
                onClick={() => {
                  if (period > 0) {
                    handleSubmit();
                  } else {
                    toast.error("Period must be greater than 0");
                  }
                }}
                disabled={selectedOptions?.length === 0}
              >
                Submit
              </button>

              {onInsertIndexData && (
                <Tooltip text={"Add data for your index"}>
                  <button
                    type="button"
                    onClick={onInsertIndexData}
                    className="h-7 px-3 bg-[#313C4C] text-white text-[11px] font-medium rounded-none whitespace-nowrap hover:bg-[#1e2632] transition-colors cursor-pointer"
                  >
                    Insert Index Data
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Right Control: View Route (FAR RIGHT) */}
          <div className="ml-auto flex items-center">
            <Tooltip text={"Open map view to visualize routes for the selected vessel index"}>
              <button
                type="button"
                onClick={onViewRoute}
                disabled={!selectedOptions?.length}
                className="h-7 px-3 border border-slate-300 bg-white text-[#313C4C] text-[11px] font-medium rounded-none whitespace-nowrap hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                View Route
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Date Range Picker Dialog */}
        <Dialog
          className="superadmin-dialog"
          open={openDatePicker}
          onClose={() => setOpenDatePicker(false)}
          maxWidth="md"
          aria-labelledby="market-date-range-title"
        >
          <DialogTitle id="market-date-range-title" className="!px-3 !py-1.5 !text-[14px] !font-medium bg-slate-50 border-b border-slate-200">Select Date Range</DialogTitle>
          <DialogContent className="!p-2 scroll-thin [&_.rdrDateRangePickerWrapper]:flex-wrap [&_.rdrDefinedRangesWrapper]:w-full sm:[&_.rdrDefinedRangesWrapper]:w-[180px]">
            <DateRangePicker
              onChange={handleDateRangeSelect}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              rangeColors={["#313C4C"]}
              locale={locales[locale]}
              months={1}
              direction="horizontal"
              showSelectionPreview={true}
              editableDateInputs={true}
              dragSelectionEnabled={true}
            />
          </DialogContent>
          <div className="flex justify-end gap-2 px-3 py-1.5 bg-slate-50 border-t border-slate-200">
            <button
              onClick={() => setOpenDatePicker(false)}
              className="h-7 px-3 text-[11px] font-medium text-slate-700 bg-white border border-slate-300 rounded-none hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyDateRange}
              className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] rounded-none hover:bg-[#1e2632]"
            >
              Apply
            </button>
          </div>
        </Dialog>
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {smaOptions?.series?.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={smaOptions} />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the chart
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading chart...
          </div>
        )}
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {dailyChangeOptions?.series?.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={dailyChangeOptions}
          />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the chart
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading chart...
          </div>
        )}
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {volatilityOptions?.series?.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={volatilityOptions}
          />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the chart
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading chart...
          </div>
        )}
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {heatmapOptions?.series?.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={heatmapOptions} />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the heatmap
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading heatmap...
          </div>
        )}
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {seasonalOptions?.series?.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={seasonalOptions}
          />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the chart
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading chart...
          </div>
        )}
      </div>

      <div className="border border-slate-300 rounded-none p-1 shadow-sm mb-1 bg-white">
        {seasonalHeatmapOptions?.series?.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={seasonalHeatmapOptions}
          />
        ) : selectedOptions.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Please select at least one option to display the chart
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            No data available
          </div>
        ) : (
          <div className="text-center p-3 text-slate-500 text-[11px]">
            Loading chart...
          </div>
        )}
      </div>
    </div>
  );
};

export default MovingAverageChart;
